import {
  convertEmptyStringsToNull,
  convertIncomingPercentage,
  convertOutgoingPercentage,
  filterObjectProps,
} from "@shared/modulePayload";
import { TagTypes } from "../../../apiSlice";

/**
 * Transforms API response data into ModuleFull structure
 */
export function transformModuleResponse<ResponseType extends { [key: string]: any }, MF>({
  response,
  includedPropList,
  includedT2PropList,
  percentageProps = [],
  t2PercentageProps = [],
  extraTransformLogic
}: {
  response: ResponseType,
  includedPropList?: string[],
  includedT2PropList?: string[],
  percentageProps?: string[],
  t2PercentageProps?: string[],
  extraTransformLogic?: (module: any, t2: any) => void
}
) {
  if (response) {
    const module = includedPropList ? filterObjectProps(
      { ...response },
      includedPropList
    ) : response;

    const t2 = includedT2PropList ? filterObjectProps(
      { ...response },
      includedT2PropList
    ) : response;

    if (percentageProps.length) {
      convertIncomingPercentage(module, percentageProps);
    }

    if (t2PercentageProps.length) {
      convertIncomingPercentage(t2, t2PercentageProps);
    }

    if (extraTransformLogic) {
      extraTransformLogic(module, t2);
    }

    return {
      module,
      tiertwo: t2,
    } as MF;
  }
  return null;
}

/**
 * Prepares module data for update/create API call
 */
export function prepareModulePayload({
  moduleData,
  tiertwoData,
  percentageProps = [],
  t2PercentageProps = [],
  activityId,
}: {
  moduleData: any,
  tiertwoData: any,
  percentageProps?: string[],
  t2PercentageProps?: string[],
  activityId: number,
}) {
  const module = { ...moduleData };
  const tiertwo = { ...tiertwoData };
  const lucId = module.land_use_change;

  if (percentageProps.length) {
    convertOutgoingPercentage(module, percentageProps);
  }

  if (t2PercentageProps.length) {
    convertOutgoingPercentage(tiertwo, t2PercentageProps);
  }

  const payload = {
    ...module,
    ...tiertwo,
  };

  const { id, ...rest } = payload;
  const body = { ...rest, activity: activityId, land_use_change: lucId };

  convertEmptyStringsToNull(body);

  return { body, id };
}

/**
 * Generates common invalidation tags for module APIs
 */
export function getCommonInvalidationTags({
  results,
  moduleId,
  activityId,
  moduleTag,
  endpoint,
  includesLandUseChange = false,
  tagDefaults,
}: {
  results: any,
  moduleId: number,
  activityId: number,
  moduleTag: TagTypes,
  endpoint: string,
  includesLandUseChange?: boolean,
  tagDefaults?: TagTypes,
}): { type: TagTypes, id: number | string | undefined }[] {
  const tags: { type: TagTypes, id: number | string | undefined }[] = [
    { type: moduleTag, id: moduleId },
    ...(tagDefaults ? [{ type: tagDefaults, id: moduleId }] : []),
    { type: "ModulesStatuses" as TagTypes, id: activityId },
    { type: "ModuleResults" as TagTypes, id: results?.id ? `${endpoint}-${results?.id}` : undefined },
    { type: "ModuleHistory" as TagTypes, id: results?.id ? `${endpoint}-${results?.id}` : undefined },
  ];

  // Add land use change tag if needed
  if (includesLandUseChange) {
    tags.push({ type: "ModuleResults" as TagTypes, id: "land-use-changes" });
  }

  return tags;
}
